import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface RecentActivityItem {
  id: string;
  actionName: string;
  description: string | null;
  createdAt: string;
  relativeTime: string;
}

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export const useRecentActivities = () => {
  const { user } = useAuth();

  const { data: activities = [], isLoading } = useQuery({
    queryKey: ["recent-activities", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("activity_log")
        .select("id, action_name, description, created_at, activity_type")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) {
        console.error("[useRecentActivities] Error:", error.message);
        return [];
      }

      return (data ?? []).map((row): RecentActivityItem => ({
        id: row.id,
        actionName: row.action_name,
        description: row.description,
        createdAt: row.created_at,
        relativeTime: formatRelativeTime(row.created_at),
      }));
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const count = activities.length;

  return { activities, count, isLoading };
};
